import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { getAccessToken } from "@/lib/safeStorage";
import {
  Users,
  Image as ImageIcon,
  Send as SendIcon,
  Plus,
  Trash2,
  Loader2,
  ChevronRight,
  ShieldCheck,
  Activity as ActivityIcon,
  TrendingUp,
  BarChart3,
  Sparkles,
  Eye,
  Download,
  Star,
  Inbox,
  ClipboardCheck,
  Layers,
  Shield,
  Link as LinkIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import CatalogManager from "@/components/CatalogManager";
import ProductUrlsManager from "@/components/ProductUrlsManager";
import StyleCategoryMatrix from "@/components/StyleCategoryMatrix";
import PromptManager from "@/components/PromptManager";
import VisionFeedback from "@/components/VisionFeedback";
import AdminVisionAiStats from "@/components/AdminVisionAiStats";
import EmailHealthWidget from "@/components/EmailHealthWidget";
import WantedProfilesTab from "@/components/WantedProfilesTab";
import CatalogAuditTab from "@/components/CatalogAuditTab";
import PhoneInputIntl, { splitInternationalPhone } from "@/components/PhoneInputIntl";
import { RO_COUNTIES, RO_COUNTY_BY_CODE as ROCC } from "@/lib/romaniaCounties";
import UserCleanupDialog from "@/components/UserCleanupDialog";
import ActivityHeatmap from "@/components/ActivityHeatmap";
import PermissionsEditor from "@/components/PermissionsEditor";
import AdminTrafficStats from "@/components/AdminTrafficStats";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

const STYLE_COLORS = {
  ART_DECO: "#EE7440",
  CLASSIC: "#fbbf24",
  MODERN: "#a3a3a3",
  NEOCLASSIC: "#F4AC87",
};

// Recharts style constants — hoisted at module scope so referential identity
// is stable across renders. Inline {…} objects in chart props were forcing
// Recharts to recompute layout on every parent render (admin dashboard polls
// every 30s, so this was a hot path). Iter 67.
const RECHARTS_TOOLTIP_STYLE = {
  background: "#0a0a0a",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "white",
};
const RECHARTS_BAR_CURSOR = { fill: "rgba(238,116,64,0.08)" };
const RECHARTS_BAR_RADIUS = [6, 6, 0, 0];
const RECHARTS_LINE_DOT = { fill: "#EE7440", r: 3 };

const ACT_LABEL = {
  login: "Login",
  register: "Înregistrare",
  simulation_created: "Simulare creată",
  recommendation_sent: "Recomandare trimisă",
};

// Filter the sims list by the controls in the Sims tab (feedback 22.05.2026).
// Computed inline at render-time — list is small (<10k) so no perf concern.
function applySimFilters(sims, users, filters) {
  const userById = new Map(users.map((u) => [u.id, u]));
  const userByEmail = new Map(users.map((u) => [(u.email || "").toLowerCase(), u]));
  const roleOf = (s) => {
    // Prefer the server-enriched user_role (admin/simulations returns this);
    // fall back to local users[] lookup by id or email; finally default to
    // simple_user so unknown rows still show in the most permissive bucket.
    if (s.user_role) return s.user_role;
    const u = userById.get(s.user_id) || userByEmail.get((s.user_email || "").toLowerCase());
    return u?.role || "simple_user";
  };
  const now = new Date();
  const cutoff = (() => {
    if (filters.period === "today") {
      const t = new Date(now);
      t.setHours(0, 0, 0, 0);
      return t.getTime();
    }
    if (filters.period === "7d") return now.getTime() - 7 * 24 * 3600 * 1000;
    if (filters.period === "30d") return now.getTime() - 30 * 24 * 3600 * 1000;
    return null;
  })();
  const q = (filters.search || "").trim().toLowerCase();
  return sims.filter((s) => {
    const role = roleOf(s);
    if (filters.role !== "all" && role !== filters.role) return false;
    if (filters.sim_type !== "all" && s.sim_type !== filters.sim_type) return false;
    if (filters.status !== "all" && s.status !== filters.status) return false;
    if (cutoff !== null) {
      const ts = new Date(s.created_at).getTime();
      if (!Number.isFinite(ts) || ts < cutoff) return false;
    }
    if (q) {
      const u = userById.get(s.user_id) || userByEmail.get((s.user_email || "").toLowerCase());
      const hay = [
        s.user_email || "",
        s.style || "",
        (s.selected_categories || []).join(" "),
        u?.name || "",
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function formatDate(iso) {
  return new Date(iso).toLocaleString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-5">
      <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div
        className={`font-outfit font-black text-3xl mt-2 tracking-tighter ${
          accent ? "text-orange-500" : "text-white"
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

function Bar2({ label, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="text-xs text-zinc-300 w-44 truncate">{label}</div>
      <div className="flex-1 h-7 bg-zinc-900 rounded relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-600 to-orange-400 flex items-center justify-end pr-2"
          style={{ width: `${Math.max(8, pct)}%` }}
        >
          <span className="text-[11px] font-bold text-white">{count}</span>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sims, setSims] = useState([]);
  const [recs, setRecs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [quizInsights, setQuizInsights] = useState(null);
  const [ratingsInsights, setRatingsInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "partner",
    company: "",
    phone: "",
    country_code: "RO",
    county_code: "",
  });

  const [drillUserId, setDrillUserId] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [confirmDeleteSim, setConfirmDeleteSim] = useState(null);
  const [deletingSim, setDeletingSim] = useState(false);
  const [bulkCleanupOpen, setBulkCleanupOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkFilter, setBulkFilter] = useState({ older_than_days: "", user_email: "" });
  // iter 95 · Permissions editor (per-user granular ACL)
  const [permsEditorUser, setPermsEditorUser] = useState(null);
  const [permsEditorOpen, setPermsEditorOpen] = useState(false);

  // Sims tab filters (feedback 22.05.2026)
  const [simFilters, setSimFilters] = useState({
    role: "all", // "all" | "simple_user" | "partner" | "sales" | "admin"
    period: "all", // "all" | "today" | "7d" | "30d"
    sim_type: "all", // "all" | "INTERIOR" | "EXTERIOR"
    status: "all", // "all" | "completed" | "processing" | "failed"
    search: "", // free-text on user_email / style / categories
  });

  // Stats tab filters (feedback Marius 03.06.2026) — period + sim_type
  // affect ONLY style popularity / categories popularity / sim counts.
  // User totals and active counts remain all-time for accurate KPIs.
  const [statsFilters, setStatsFilters] = useState({
    since_days: 0, // 0 = all time, 7 / 30 / 90
    sim_type: "all", // "all" | "INTERIOR" | "EXTERIOR"
  });

  const fetchAll = useCallback(async () => {
    try {
      const statsParams = new URLSearchParams();
      if (statsFilters.since_days > 0) statsParams.set("since_days", String(statsFilters.since_days));
      if (statsFilters.sim_type !== "all") statsParams.set("sim_type", statsFilters.sim_type);
      const statsUrl = `/admin/stats${statsParams.toString() ? `?${statsParams}` : ""}`;
      const [s, u, sm, rc, ac, qi, ri] = await Promise.all([
        api.get(statsUrl),
        api.get("/admin/users"),
        api.get("/admin/simulations"),
        api.get("/admin/recommendations"),
        api.get("/admin/activity?limit=80"),
        api.get("/quiz/insights"),
        api.get("/admin/ratings/insights"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setSims(sm.data);
      setRecs(rc.data);
      setActivity(ac.data);
      setQuizInsights(qi.data);
      setRatingsInsights(ri.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("admin.load_error"));
    } finally {
      setLoading(false);
    }
  }, [statsFilters.since_days, statsFilters.sim_type, t]); // re-fetch when stats filters change

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [fetchAll]);

  const submitCreate = async () => {
    setCreating(true);
    try {
      await api.post("/admin/users", newUser);
      toast.success(t("admin.create_success", { role: newUser.role }));
      setCreateOpen(false);
      setNewUser({ email: "", password: "", name: "", role: "partner", company: "", phone: "" });
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("admin.create_error"));
    } finally {
      setCreating(false);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Sigur vrei să ștergi acest user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(t("admin.delete_user_success"));
      fetchAll();
    } catch (e) {
      toast.error(t("admin.delete_user_error"));
    }
  };

  const doDeleteSim = async () => {
    if (!confirmDeleteSim) return;
    setDeletingSim(true);
    const simId = confirmDeleteSim.id;
    try {
      await api.delete(`/simulations/${simId}`);
      setSims((arr) => arr.filter((x) => x.id !== simId));
      toast.success(t("admin.delete_sim_success"));
      setConfirmDeleteSim(null);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : t("admin.delete_sim_error"));
    } finally {
      setDeletingSim(false);
    }
  };

  const openBulkCleanup = async () => {
    setBulkCleanupOpen(true);
    setBulkPreview(null);
    setBulkFilter({ older_than_days: "", user_email: "" });
    await runBulkPreview({});
  };

  const runBulkPreview = async (filter) => {
    setBulkLoading(true);
    try {
      const body = { dry_run: true };
      if (filter.older_than_days !== "" && filter.older_than_days != null) {
        body.older_than_days = Number(filter.older_than_days);
      }
      if (filter.user_email) body.user_email = filter.user_email.trim();
      const r = await api.post("/admin/simulations/bulk-cleanup", body);
      setBulkPreview(r.data);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : t("admin.preview_error"));
      setBulkPreview(null);
    } finally {
      setBulkLoading(false);
    }
  };

  const doBulkCleanup = async () => {
    setBulkLoading(true);
    try {
      const body = { dry_run: false };
      if (bulkFilter.older_than_days !== "" && bulkFilter.older_than_days != null) {
        body.older_than_days = Number(bulkFilter.older_than_days);
      }
      if (bulkFilter.user_email) body.user_email = bulkFilter.user_email.trim();
      const r = await api.post("/admin/simulations/bulk-cleanup", body);
      toast.success(
        `${r.data.deleted_sims} simulări șterse · ${r.data.deleted_storage_objects} fișiere storage curățate`
      );
      setBulkCleanupOpen(false);
      setBulkPreview(null);
      fetchAll();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : t("admin.cleanup_error"));
    } finally {
      setBulkLoading(false);
    }
  };

  const openDrill = async (userId) => {
    setDrillUserId(userId);
    setDrillData(null);
    try {
      const { data } = await api.get(`/admin/users/${userId}/details`);
      setDrillData(data);
    } catch (e) {
      toast.error(t("admin.preview_details_error"));
    }
  };

  const exportData = async (kind, filename, format = "csv") => {
    try {
      const token = getAccessToken();
      const resp = await fetch(`${API_BASE}/admin/export/${kind}.${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("admin.export_success", { kind, format: format.toUpperCase() }));
    } catch (e) {
      toast.error(t("admin.export_error", { kind }));
    }
  };

  // Iter 66 (feedback 22.05.2026): both CSV and XLSX are supported. Default
  // download is XLSX (what client asked for); CSV remains as fallback. The
  // button itself triggers XLSX; right-click / long-press surfaces an explicit
  // CSV button next to it via the `withCsvFallback` prop.
  const ExportBtn = ({ kind, filename, label, withCsvFallback = true }) => {
    const xlsxName = filename.replace(/\.csv$/i, ".xlsx");
    return (
      <div className="inline-flex items-stretch rounded overflow-hidden">
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportData(kind, xlsxName, "xlsx")}
          className="rounded-r-none bg-zinc-900/50 border-white/10 text-white hover:bg-orange-500/10 hover:border-orange-500/30 hover:text-orange-400"
          data-testid={`export-${kind}-btn`}
        >
          <Download size={14} className="mr-2" /> {label}
        </Button>
        {withCsvFallback && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportData(kind, filename, "csv")}
            className="rounded-l-none border-l-0 bg-zinc-900/50 border-white/10 text-zinc-400 hover:bg-orange-500/5 hover:text-orange-400 px-2 text-[10px] uppercase tracking-[0.15em]"
            data-testid={`export-${kind}-csv-btn`}
            title="Descarcă CSV în loc de XLSX"
          >
            csv
          </Button>
        )}
      </div>
    );
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="text-orange-500 animate-spin" size={40} />
      </div>
    );
  }

  // Charts data
  const styleData = Object.entries(stats.simulations.by_style).map(([k, v]) => ({
    name: k.replace("_", " "),
    value: v,
    fill: STYLE_COLORS[k] || "#F4AC87",
  }));
  const typeData = [
    { name: "Interior", value: stats.simulations.by_type.INTERIOR, fill: "#EE7440" },
    { name: "Exterior", value: stats.simulations.by_type.EXTERIOR, fill: "#404040" },
  ];
  const dailyData = stats.simulations.daily_last_14d.map((d) => ({
    day: d.day.slice(5),
    count: d.count,
  }));
  const maxCat = Math.max(1, ...stats.categories_popularity.map((c) => c.count));

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-20">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3 flex items-center gap-2">
              <ShieldCheck size={12} strokeWidth={1.5} /> {t("admin.header_eyebrow")}
            </div>
            <h1 className="font-outfit font-black text-white text-5xl md:text-6xl tracking-tighter leading-none">
              {t("admin.header_title")}
            </h1>
            <p className="text-zinc-400 mt-3">
              {t("admin.header_subtitle")}
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-orange-500 hover:bg-orange-600 text-white btn-orange-glow h-12 px-6"
                data-testid="open-create-user-btn"
              >
                <Plus size={16} className="mr-2" /> {t("admin.create_user_btn")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-outfit text-2xl tracking-tighter">
                  {t("admin.create_user_title")}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("admin.create_field_name")}</Label>
                    <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="bg-zinc-900/50 border-white/10 mt-2" data-testid="cu-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("admin.create_field_role")}</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger className="bg-zinc-900/50 border-white/10 mt-2" data-testid="cu-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-white/10 text-white">
                        <SelectItem value="partner">Partner</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("admin.create_field_email")}</Label>
                  <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="bg-zinc-900/50 border-white/10 mt-2" data-testid="cu-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("admin.create_field_password")}</Label>
                  <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="bg-zinc-900/50 border-white/10 mt-2" data-testid="cu-password-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("admin.create_field_company")}</Label>
                    <Input value={newUser.company} onChange={(e) => setNewUser({ ...newUser, company: e.target.value })} className="bg-zinc-900/50 border-white/10 mt-2" data-testid="cu-company-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("admin.create_field_phone")}</Label>
                    <div className="mt-2">
                      <PhoneInputIntl
                        value={newUser.phone}
                        onChange={(v) => {
                          const { code } = splitInternationalPhone(v);
                          setNewUser((u) => ({
                            ...u,
                            phone: v,
                            country_code: code,
                            county_code: code === "RO" ? u.county_code : "",
                          }));
                        }}
                        testid="cu-phone"
                      />
                    </div>
                  </div>
                </div>
                {newUser.country_code === "RO" ? (
                  <div data-testid="cu-county-block">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">
                      {t("admin.create_field_county")} <span className="text-orange-400">*</span>
                    </Label>
                    <select
                      value={newUser.county_code || ""}
                      onChange={(e) => setNewUser({ ...newUser, county_code: e.target.value })}
                      className="w-full mt-2 h-10 rounded-md bg-zinc-900/50 border border-white/10 text-white text-sm px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                      data-testid="cu-county-select"
                    >
                      <option value="">{t("admin.create_field_county_placeholder")}</option>
                      {RO_COUNTIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div data-testid="cu-country-block" className="text-xs text-zinc-400">
                    {t("admin.create_field_country_label")} <span className="text-white font-medium ml-1">{newUser.country_code}</span>
                  </div>
                )}
                <Button onClick={submitCreate} disabled={creating} className="w-full bg-orange-500 hover:bg-orange-600 mt-4" data-testid="cu-submit-btn">
                  {creating ? <Loader2 size={16} className="animate-spin" /> : t("admin.create_submit")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* KPI grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-grid">
          <KPI label={t("admin.kpi_users_total")} value={stats.users.total} sub={t("admin.kpi_users_total_sub", { n: stats.users.new_last_7d })} />
          <KPI label={t("admin.kpi_active_24h")} value={stats.users.active_last_24h} sub={t("admin.kpi_active_24h_sub", { n: stats.users.active_last_7d })} accent />
          <KPI label={t("admin.kpi_sims_total")} value={stats.simulations.total} sub={t("admin.kpi_sims_total_sub", { today: stats.simulations.last_24h, week: stats.simulations.last_7d })} />
          <KPI label={t("admin.kpi_completion")} value={`${Math.round(stats.simulations.completion_rate * 100)}%`} sub={t("admin.kpi_completion_sub", { ok: stats.simulations.by_status.completed, fail: stats.simulations.by_status.failed })} />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <KPI label={t("admin.kpi_simple_users")} value={stats.users.by_role.simple_user} />
          <KPI label={t("admin.kpi_partners")} value={stats.users.by_role.partner} accent />
          <KPI label={t("admin.kpi_sales")} value={stats.users.by_role.sales} accent />
          <KPI label={t("admin.kpi_recs_sent")} value={stats.recommendations.total} sub={t("admin.kpi_recs_sent_sub", { n: stats.recommendations.last_7d })} />
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-zinc-900/40 border border-white/10 p-1 inline-flex flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-overview">
              <BarChart3 size={14} className="mr-2" /> {t("admin.tab_overview")}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-users">
              <Users size={14} className="mr-2" /> {t("admin.tab_users")}
            </TabsTrigger>
            <TabsTrigger value="sims" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-simulations">
              <ImageIcon size={14} className="mr-2" /> {t("admin.tab_simulations")}
            </TabsTrigger>
            <TabsTrigger value="recs" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-recommendations">
              <SendIcon size={14} className="mr-2" /> {t("admin.tab_recommendations")}
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-quote-requests">
              <SendIcon size={14} className="mr-2" /> {t("admin.tab_quotes")}
            </TabsTrigger>
            <TabsTrigger value="erp" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-erp">
              <SendIcon size={14} className="mr-2" /> {t("admin.tab_erp")}
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-activity">
              <ActivityIcon size={14} className="mr-2" /> {t("admin.tab_activity")}
            </TabsTrigger>
            <TabsTrigger value="catalog" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-catalog">
              <ImageIcon size={14} className="mr-2" /> {t("admin.tab_catalog")}
            </TabsTrigger>
            <TabsTrigger value="product-urls" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-product-urls">
              <LinkIcon size={14} className="mr-2" /> URL-uri produse
            </TabsTrigger>
            <TabsTrigger value="style-matrix" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-style-matrix">
              <Layers size={14} className="mr-2" /> {t("admin.tab_style_matrix")}
            </TabsTrigger>
            <TabsTrigger value="prompts" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-prompts">
              <Sparkles size={14} className="mr-2" /> {t("admin.tab_prompts")}
            </TabsTrigger>
            <TabsTrigger value="vision" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-vision">
              <Sparkles size={14} className="mr-2" /> {t("admin.tab_vision")}
            </TabsTrigger>
            <TabsTrigger value="vision-ai" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-vision-ai">
              <Sparkles size={14} className="mr-2" /> {t("vision_ai.admin_tab_label")}
            </TabsTrigger>
            <TabsTrigger value="wanted" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-wanted">
              <Inbox size={14} className="mr-2" /> {t("admin.tab_wanted")}
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-audit">
              <ClipboardCheck size={14} className="mr-2" /> {t("admin.tab_audit")}
            </TabsTrigger>
            <TabsTrigger value="traffic" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-traffic">
              <ActivityIcon size={14} className="mr-2" /> Trafic
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW / ANALYTICS */}
          <TabsContent value="overview" className="mt-6 space-y-6" data-testid="overview-content">
            <div className="flex items-center justify-end">
              <UserCleanupDialog />
            </div>
            {/* Email send health (24h) — iter 82. Surfaces Brevo / SMTP
                deliverability issues immediately so admins don't wait for
                customer complaints to find out the verification flow is
                broken. */}
            <EmailHealthWidget />
            {/* Filtre Stats — period + sim_type (Marius feedback 03.06.2026) */}
            <div
              className="border border-white/10 rounded-lg bg-zinc-900/30 p-3 flex flex-wrap items-center gap-3 text-xs"
              data-testid="admin-stats-filters"
            >
              <span className="text-zinc-400 font-medium uppercase tracking-wider text-[10px]">
                Filtre statistici:
              </span>
              <div className="flex items-center gap-1">
                <span className="text-zinc-500">Perioadă</span>
                <select
                  value={statsFilters.since_days}
                  onChange={(e) =>
                    setStatsFilters((f) => ({ ...f, since_days: Number(e.target.value) }))
                  }
                  className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-xs"
                  data-testid="admin-stats-period-select"
                >
                  <option value={0}>Tot timpul</option>
                  <option value={7}>Ultimele 7 zile</option>
                  <option value={30}>Ultimele 30 zile</option>
                  <option value={90}>Ultimele 90 zile</option>
                </select>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-zinc-500">Tip simulare</span>
                <select
                  value={statsFilters.sim_type}
                  onChange={(e) =>
                    setStatsFilters((f) => ({ ...f, sim_type: e.target.value }))
                  }
                  className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-xs"
                  data-testid="admin-stats-simtype-select"
                >
                  <option value="all">Toate</option>
                  <option value="INTERIOR">Doar INTERIOR</option>
                  <option value="EXTERIOR">Doar EXTERIOR</option>
                </select>
              </div>
              <span className="text-[10px] text-zinc-500 ml-auto italic">
                Afectează: Stiluri preferate, Categorii preferate, conturi simulări.
                NU afectează: total utilizatori activi.
              </span>
            </div>
            {/* Charts row */}
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-outfit font-bold text-white">{t("admin.section_favorite_styles")}</h3>
                  <Sparkles size={14} className="text-orange-500" />
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <BarChart data={styleData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
                      <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                      <RTooltip
                        cursor={RECHARTS_BAR_CURSOR}
                        contentStyle={RECHARTS_TOOLTIP_STYLE}
                      />
                      <Bar dataKey="value" radius={RECHARTS_BAR_RADIUS} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-outfit font-bold text-white">{t("admin.section_interior_vs_exterior")}</h3>
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <PieChart>
                      <Pie data={typeData} dataKey="value" innerRadius={50} outerRadius={80} stroke="#0a0a0a">
                        {typeData.map((d) => (
                          <Cell key={d.name || d.fill} fill={d.fill} />
                        ))}
                      </Pie>
                      <RTooltip
                        contentStyle={RECHARTS_TOOLTIP_STYLE}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-5 text-xs text-zinc-400">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 bg-orange-500 rounded-sm" /> Interior {typeData[0].value}</span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 bg-zinc-700 rounded-sm" /> Exterior {typeData[1].value}</span>
                </div>
              </div>

              <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-outfit font-bold text-white">{t("admin.section_activity_14d")}</h3>
                  <TrendingUp size={14} className="text-orange-500" />
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <LineChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="day" stroke="#71717a" fontSize={10} />
                      <YAxis stroke="#71717a" fontSize={10} allowDecimals={false} />
                      <RTooltip contentStyle={RECHARTS_TOOLTIP_STYLE} />
                      <Line type="monotone" dataKey="count" stroke="#EE7440" strokeWidth={2} dot={RECHARTS_LINE_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Categories popularity */}
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 border border-white/10 rounded-lg bg-zinc-900/30 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-outfit font-bold text-white text-lg">{t("admin.section_top_categories")}</h3>
                  <span className="text-xs uppercase tracking-[0.25em] text-zinc-500">
                    {stats.categories_popularity.length} înregistrări
                  </span>
                </div>
                {stats.categories_popularity.length === 0 ? (
                  <div className="text-zinc-500 text-sm py-6 text-center">
                    Nicio categorie selectată încă. Apare aici când Partner / Sales creează simulări manuale.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {stats.categories_popularity.map((c) => (
                      <Bar2 key={c.slug} label={`${c.name} · ${c.type}`} count={c.count} max={maxCat} />
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-6">
                <h3 className="font-outfit font-bold text-white text-lg mb-5">{t("admin.section_top_partners")}</h3>
                {stats.top_partners.length === 0 ? (
                  <div className="text-zinc-500 text-sm">Niciun partener activ încă.</div>
                ) : (
                  <div className="space-y-3">
                    {stats.top_partners.map((p) => (
                      <button
                        key={p.user_id}
                        onClick={() => openDrill(p.user_id)}
                        className="w-full text-left flex items-center justify-between p-3 border border-white/5 rounded-md hover:border-orange-500/30 hover:bg-zinc-900/50 transition"
                        data-testid={`top-partner-${p.user_id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-white text-sm truncate">{p.name || p.email}</div>
                          <div className="text-xs text-zinc-500 truncate">{p.company || p.email}</div>
                        </div>
                        <div className="text-right ml-3">
                          <div className="font-outfit text-xl font-black text-orange-500">{p.simulations}</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">sim · {p.recommendations} rec</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <h3 className="font-outfit font-bold text-white text-lg mt-8 mb-5">{t("admin.section_top_sales")}</h3>
                {stats.top_sales.length === 0 ? (
                  <div className="text-zinc-500 text-sm">Niciun sales activ încă.</div>
                ) : (
                  <div className="space-y-3">
                    {stats.top_sales.map((s) => (
                      <button
                        key={s.user_id}
                        onClick={() => openDrill(s.user_id)}
                        className="w-full text-left flex items-center justify-between p-3 border border-white/5 rounded-md hover:border-orange-500/30 hover:bg-zinc-900/50 transition"
                        data-testid={`top-sales-${s.user_id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-white text-sm truncate">{s.name || s.email}</div>
                          <div className="text-xs text-zinc-500 truncate">{s.email}</div>
                        </div>
                        <div className="text-right ml-3">
                          <div className="font-outfit text-xl font-black text-orange-500">{s.simulations}</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">simulări</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Activity heatmap */}
            <ActivityHeatmap />

            {/* Quiz insights */}
            {quizInsights && quizInsights.total_responses > 0 && (
              <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-6" data-testid="quiz-insights-card">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-1 flex items-center gap-2">
                      <Sparkles size={12} className="animate-pulse" /> Quiz Preferințe Clienți
                    </div>
                    <h3 className="font-outfit font-bold text-white text-lg">
                      {quizInsights.total_responses} răspunsuri colectate · insights pentru Sales
                    </h3>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { title: "Stiluri preferate", data: quizInsights.by_style, format: (v) => v.replace("_", " ") },
                    { title: "Element favorit", data: quizInsights.by_element, format: (v) => v.replace("_", " ") },
                    { title: "Spațiu vizat", data: quizInsights.by_space, format: (v) => v },
                    { title: "Buget", data: quizInsights.by_budget, format: (v) => v },
                  ].map((col) => {
                    const m = Math.max(1, ...col.data.map((d) => d.count));
                    return (
                      <div key={col.title}>
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                          {col.title}
                        </div>
                        {col.data.length === 0 ? (
                          <div className="text-zinc-600 text-xs">—</div>
                        ) : (
                          <div className="space-y-1.5">
                            {col.data.slice(0, 4).map((d) => (
                              <div key={d.value} className="flex items-center gap-2">
                                <div className="text-xs text-zinc-300 w-28 truncate">
                                  {col.format(d.value)}
                                </div>
                                <div className="flex-1 h-5 bg-zinc-900 rounded relative overflow-hidden">
                                  <div
                                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-600 to-orange-400 flex items-center justify-end pr-1.5"
                                    style={{ width: `${Math.max(10, (d.count / m) * 100)}%` }}
                                  >
                                    <span className="text-[10px] font-bold text-white">{d.count}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ratings insights */}
            {ratingsInsights && ratingsInsights.overall.total > 0 && (
              <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-6" data-testid="ratings-insights-card">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-1 flex items-center gap-2">
                      <Star size={12} className="fill-orange-400 text-orange-400" />
                      Rating fidelitate randări
                    </div>
                    <h3 className="font-outfit font-bold text-white text-lg">
                      {ratingsInsights.overall.total} ratings · medie generală{" "}
                      <span className="text-orange-400">{ratingsInsights.overall.avg}★</span>
                    </h3>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { title: "Pe motor AI", data: ratingsInsights.by_engine, format: (v) => v.replace("_", " ") },
                    { title: "Pe stil", data: ratingsInsights.by_style, format: (v) => v.replace("_", " ") },
                    { title: "Tip sim.", data: ratingsInsights.by_type, format: (v) => v },
                    { title: "Pe variație", data: ratingsInsights.by_variation, format: (v) => `Var. ${(v ?? 0) + 1}` },
                  ].map((col) => (
                    <div key={col.title}>
                      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                        {col.title}
                      </div>
                      {col.data.length === 0 ? (
                        <div className="text-zinc-600 text-xs">—</div>
                      ) : (
                        <div className="space-y-1.5">
                          {col.data.slice(0, 5).map((d) => (
                            <div key={String(d.value)} className="flex items-center gap-2 text-xs">
                              <div className="text-zinc-300 flex-1 truncate">
                                {col.format(d.value)}
                              </div>
                              <div className="flex items-center gap-0.5 text-orange-400">
                                <Star size={11} className="fill-orange-400" />
                                <span className="font-bold tabular-nums">{d.avg}</span>
                              </div>
                              <div className="text-zinc-500 tabular-nums">
                                ({d.count})
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Export bar */}
            <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-5 flex flex-wrap items-center gap-3" data-testid="export-bar">
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs uppercase tracking-[0.25em] text-orange-500 mb-1">
                  Export date · CSV / Excel
                </div>
                <div className="text-zinc-400 text-sm">
                  Descarcă rapoarte deschise direct în Excel, Google Sheets sau LibreOffice.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <ExportBtn kind="users" filename="fdp_utilizatori.csv" label="Utilizatori" />
                <ExportBtn kind="simulations" filename="fdp_simulari.csv" label="Simulări" />
                <ExportBtn kind="categories" filename="fdp_top_categorii.csv" label="Top categorii" />
                <ExportBtn kind="recommendations" filename="fdp_recomandari.csv" label="Recomandări" />
                <ExportBtn kind="activity" filename="fdp_activitate.csv" label="Activitate" />
              </div>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-6 space-y-3">
            <div className="flex justify-end">
              <ExportBtn kind="users" filename="fdp_utilizatori.csv" label="Export CSV utilizatori" />
            </div>
            <div className="border border-white/10 rounded-lg bg-zinc-900/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/60 text-zinc-400 uppercase tracking-[0.2em] text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Nume</th>
                    <th className="text-left px-4 py-3">Rol</th>
                    <th className="text-left px-4 py-3">Țară / Județ</th>
                    <th className="text-left px-4 py-3">Ultimul login</th>
                    <th className="text-left px-4 py-3">Logins</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody data-testid="users-table-body">
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5 hover:bg-zinc-900/40">
                      <td className="px-4 py-3 text-white">{u.email}</td>
                      <td className="px-4 py-3 text-zinc-300">{u.name}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.15em] border ${
                            u.role === "admin"
                              ? "border-orange-500/30 text-orange-400 bg-orange-500/10"
                              : "border-white/10 text-zinc-300"
                          }`}
                        >
                          {u.role.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-300 text-xs">
                        {u.country_code === "RO" && u.county_code
                          ? `🇷🇴 ${(ROCC[u.county_code] || u.county_code)}`
                          : u.country_code && u.country_code !== "RO"
                          ? `${u.country_name || u.country_code}`
                          : u.country_code === "RO"
                          ? "🇷🇴 RO"
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-zinc-500">
                        {u.last_login_at ? formatDate(u.last_login_at) : "—"}
                      </td>
                      <td className="px-4 py-3 text-zinc-400">{u.login_count || 0}</td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDrill(u.id)}
                          className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                          data-testid={`drill-user-${u.id}`}
                        >
                          <Eye size={14} />
                        </Button>
                        {u.role !== "admin" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPermsEditorUser(u);
                              setPermsEditorOpen(true);
                            }}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`perms-user-${u.id}`}
                            title="Editează permisii"
                          >
                            <Shield size={14} />
                          </Button>
                        )}
                        {u.role !== "admin" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteUser(u.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* SIMS */}
          <TabsContent value="sims" className="mt-6 space-y-3">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={simFilters.role}
                  onChange={(e) => setSimFilters((f) => ({ ...f, role: e.target.value }))}
                  className="bg-zinc-950/50 border border-white/10 text-white text-xs px-3 py-2 rounded uppercase tracking-[0.15em]"
                  data-testid="sims-filter-role"
                >
                  <option value="all">Toate rolurile</option>
                  <option value="simple_user">Simple users</option>
                  <option value="partner">Parteneri</option>
                  <option value="sales">Sales</option>
                  <option value="admin">Admin</option>
                </select>
                <select
                  value={simFilters.period}
                  onChange={(e) => setSimFilters((f) => ({ ...f, period: e.target.value }))}
                  className="bg-zinc-950/50 border border-white/10 text-white text-xs px-3 py-2 rounded uppercase tracking-[0.15em]"
                  data-testid="sims-filter-period"
                >
                  <option value="all">Toate</option>
                  <option value="today">Azi</option>
                  <option value="7d">Ultimele 7 zile</option>
                  <option value="30d">Ultimele 30 zile</option>
                </select>
                <select
                  value={simFilters.sim_type}
                  onChange={(e) => setSimFilters((f) => ({ ...f, sim_type: e.target.value }))}
                  className="bg-zinc-950/50 border border-white/10 text-white text-xs px-3 py-2 rounded uppercase tracking-[0.15em]"
                  data-testid="sims-filter-type"
                >
                  <option value="all">Toate tipurile</option>
                  <option value="INTERIOR">Interior</option>
                  <option value="EXTERIOR">Exterior</option>
                </select>
                <select
                  value={simFilters.status}
                  onChange={(e) => setSimFilters((f) => ({ ...f, status: e.target.value }))}
                  className="bg-zinc-950/50 border border-white/10 text-white text-xs px-3 py-2 rounded uppercase tracking-[0.15em]"
                  data-testid="sims-filter-status"
                >
                  <option value="all">Toate statusurile</option>
                  <option value="completed">Completate</option>
                  <option value="processing">În curs</option>
                  <option value="failed">Eșuate</option>
                </select>
                <input
                  value={simFilters.search}
                  onChange={(e) => setSimFilters((f) => ({ ...f, search: e.target.value }))}
                  placeholder="Caută email / stil…"
                  className="bg-zinc-950/50 border border-white/10 text-white text-xs px-3 py-2 rounded w-48"
                  data-testid="sims-filter-search"
                />
                {(simFilters.role !== "all" || simFilters.period !== "all" ||
                  simFilters.sim_type !== "all" || simFilters.status !== "all" ||
                  simFilters.search) && (
                  <button
                    type="button"
                    onClick={() => setSimFilters({ role: "all", period: "all", sim_type: "all", status: "all", search: "" })}
                    className="text-[10px] text-orange-400 hover:text-orange-300 uppercase tracking-[0.2em]"
                    data-testid="sims-filter-reset"
                  >
                    Resetează
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openBulkCleanup()}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500 rounded text-xs uppercase tracking-[0.2em]"
                  data-testid="open-bulk-cleanup-btn"
                >
                  <Trash2 size={14} /> Curățenie simulări
                </button>
                <ExportBtn kind="simulations" filename="fdp_simulari.csv" label="Export CSV simulări" />
              </div>
            </div>

            {/* Per-role count breakdown */}
            <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="sims-role-breakdown">
              {(() => {
                const userByEmail = new Map(users.map((u) => [(u.email || "").toLowerCase(), u]));
                const roleOf = (s) => {
                  if (s.user_role) return s.user_role;
                  const u = users.find((x) => x.id === s.user_id)
                    || userByEmail.get((s.user_email || "").toLowerCase());
                  return u?.role || "simple_user";
                };
                const counts = sims.reduce((acc, s) => {
                  const r = roleOf(s);
                  acc[r] = (acc[r] || 0) + 1;
                  return acc;
                }, {});
                const colorOf = (r) =>
                  r === "admin" ? "text-red-400" :
                  r === "sales" ? "text-orange-400" :
                  r === "partner" ? "text-yellow-400" :
                  "text-green-400";
                const labelOf = (r) =>
                  r === "simple_user" ? "Simpli" : r.charAt(0).toUpperCase() + r.slice(1);
                return ["simple_user", "partner", "sales", "admin"].map((r) => (
                  <span key={r}>
                    <span className={colorOf(r)}>●</span>{" "}
                    {labelOf(r)}: <span className="text-white font-bold">{counts[r] || 0}</span>
                  </span>
                ));
              })()}
              <span className="ml-auto text-zinc-400">
                Total afișate: <span className="text-white font-bold" data-testid="sims-visible-count">{(() => {
                  const visibleSims = applySimFilters(sims, users, simFilters);
                  return visibleSims.length;
                })()}</span> / {sims.length}
              </span>
            </div>

            <div className="border border-white/10 rounded-lg bg-zinc-900/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/60 text-zinc-400 uppercase tracking-[0.2em] text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">User</th>
                    <th className="text-left px-4 py-3">Rol</th>
                    <th className="text-left px-4 py-3">Tip</th>
                    <th className="text-left px-4 py-3">Stil</th>
                    <th className="text-left px-4 py-3">Categorii</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Creat</th>
                    <th className="text-right px-4 py-3">{t("admin.actions")}</th>
                  </tr>
                </thead>
                <tbody data-testid="sims-table-body">
                  {applySimFilters(sims, users, simFilters).map((s) => {
                    const userByEmail = users.find((x) => (x.email || "").toLowerCase() === (s.user_email || "").toLowerCase());
                    const u = users.find((x) => x.id === s.user_id) || userByEmail;
                    const role = s.user_role || u?.role || "simple_user";
                    const roleColor =
                      role === "admin" ? "text-red-400 bg-red-500/10" :
                      role === "sales" ? "text-orange-400 bg-orange-500/10" :
                      role === "partner" ? "text-yellow-400 bg-yellow-500/10" :
                      "text-green-400 bg-green-500/10";
                    return (
                    <tr key={s.id} className="border-t border-white/5 hover:bg-zinc-900/40">
                      <td className="px-4 py-3 text-white">{s.user_email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${roleColor}`}>
                          {role === "simple_user" ? "simplu" : role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">{s.sim_type}</td>
                      <td className="px-4 py-3 text-zinc-300">{s.style.replace("_", " ")}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{(s.selected_categories || []).length}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] uppercase ${
                            s.status === "completed"
                              ? "text-green-400 bg-green-500/10"
                              : s.status === "failed"
                              ? "text-red-400 bg-red-500/10"
                              : "text-orange-400 bg-orange-500/10"
                          }`}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-500">{formatDate(s.created_at)}</td>
                      <td className="px-4 py-3 text-right space-x-3">
                        <Link
                          to={`/simulation/${s.id}`}
                          className="text-orange-400 inline-flex items-center text-xs uppercase tracking-[0.2em]"
                        >
                          Vezi <ChevronRight size={14} />
                        </Link>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteSim(s)}
                          className="text-red-400 hover:text-red-300 inline-flex items-center text-xs uppercase tracking-[0.2em]"
                          data-testid={`admin-sim-delete-${s.id}`}
                        >
                          <Trash2 size={12} className="mr-1" /> Șterge
                        </button>
                      </td>
                    </tr>
                  );})}
                  {applySimFilters(sims, users, simFilters).length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-zinc-500 text-sm" data-testid="sims-empty-state">
                        Nicio simulare nu se potrivește filtrelor curente.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* RECS */}
          <TabsContent value="recs" className="mt-6 space-y-3">
            <div className="flex justify-end">
              <ExportBtn kind="recommendations" filename="fdp_recomandari.csv" label="Export CSV recomandări" />
            </div>
            <div className="border border-white/10 rounded-lg bg-zinc-900/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/60 text-zinc-400 uppercase tracking-[0.2em] text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Partner</th>
                    <th className="text-left px-4 py-3">Client</th>
                    <th className="text-left px-4 py-3">Email client</th>
                    <th className="text-left px-4 py-3">Note</th>
                    <th className="text-left px-4 py-3">Trimis</th>
                  </tr>
                </thead>
                <tbody data-testid="recs-table-body">
                  {recs.map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="px-4 py-3 text-white">{r.partner_email}</td>
                      <td className="px-4 py-3 text-zinc-300">{r.client_name}</td>
                      <td className="px-4 py-3 text-zinc-500">{r.client_email}</td>
                      <td className="px-4 py-3 text-zinc-400 max-w-xs truncate">{r.notes || "—"}</td>
                      <td className="px-4 py-3 text-zinc-500">{formatDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* QUOTE REQUESTS (Solicitări ofertă către FDP) */}
          <TabsContent value="quotes" className="mt-6 space-y-3" data-testid="quotes-content">
            <QuoteRequestsTab />
          </TabsContent>

          {/* ERP INTEGRATION (FDP OPS bidirectional sync) */}
          <TabsContent value="erp" className="mt-6 space-y-3" data-testid="erp-content">
            <ErpIntegrationTab />
          </TabsContent>

          {/* ACTIVITY FEED */}
          <TabsContent value="activity" className="mt-6 space-y-3">
            <div className="flex justify-end">
              <ExportBtn kind="activity" filename="fdp_activitate.csv" label="Export CSV activitate" />
            </div>
            <div className="border border-white/10 rounded-lg bg-zinc-900/20 overflow-hidden">
              {activity.length === 0 ? (
                <div className="p-10 text-center text-zinc-500">Niciun eveniment înregistrat încă.</div>
              ) : (
                <ul data-testid="activity-feed">
                  {activity.map((a) => (
                    <li key={a.id} className="border-t border-white/5 first:border-t-0 px-5 py-3 flex items-center gap-4 hover:bg-zinc-900/40">
                      <div className="w-2 h-2 bg-orange-500 rounded-full" />
                      <div className="text-xs uppercase tracking-[0.2em] text-orange-400 w-44">
                        {ACT_LABEL[a.type] || a.type}
                      </div>
                      <div className="flex-1 text-sm text-white truncate">
                        {a.user_email}{" "}
                        <span className="text-zinc-500">({a.user_role})</span>
                        {a.type === "simulation_created" && (
                          <span className="text-zinc-400 ml-2 text-xs">
                            {a.sim_type} · {a.style?.replace("_", " ")}
                            {a.client_name ? ` · client: ${a.client_name}` : ""}
                          </span>
                        )}
                        {a.type === "recommendation_sent" && a.client_name && (
                          <span className="text-zinc-400 ml-2 text-xs">
                            client: {a.client_name}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 whitespace-nowrap">
                        {formatDate(a.created_at)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* CATALOG FDP — admin photo uploads */}
          <TabsContent value="catalog" className="mt-6 space-y-3" data-testid="catalog-content">
            <CatalogManager />
          </TabsContent>

          <TabsContent value="product-urls" className="mt-6 space-y-3" data-testid="product-urls-content">
            <ProductUrlsManager />
          </TabsContent>

          <TabsContent value="style-matrix" className="mt-6 space-y-3" data-testid="style-matrix-content">
            <StyleCategoryMatrix />
          </TabsContent>

          <TabsContent value="prompts" className="mt-6 space-y-3" data-testid="prompts-content">
            <PromptManager />
          </TabsContent>

          <TabsContent value="vision" className="mt-6 space-y-3" data-testid="vision-content">
            <VisionFeedback />
          </TabsContent>

          <TabsContent value="vision-ai" className="mt-6 space-y-3" data-testid="vision-ai-content">
            <AdminVisionAiStats />
          </TabsContent>

          <TabsContent value="wanted" className="mt-6 space-y-3" data-testid="wanted-content">
            <WantedProfilesTab />
          </TabsContent>

          <TabsContent value="audit" className="mt-6 space-y-3" data-testid="audit-content">
            <CatalogAuditTab />
          </TabsContent>
          <TabsContent value="traffic" className="mt-6 space-y-3" data-testid="traffic-content">
            <AdminTrafficStats />
          </TabsContent>
        </Tabs>
      </main>

      {/* PERMISSIONS EDITOR — iter 95 */}
      <PermissionsEditor
        open={permsEditorOpen}
        onOpenChange={(o) => {
          setPermsEditorOpen(o);
          if (!o) setPermsEditorUser(null);
        }}
        user={permsEditorUser}
        onSaved={(updated) => {
          // Update local users list with the new permissions field
          setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
        }}
      />

      {/* USER DRILL-DOWN DIALOG */}
      <Dialog
        open={!!drillUserId}
        onOpenChange={(o) => {
          if (!o) {
            setDrillUserId(null);
            setDrillData(null);
          }
        }}
      >
        <DialogContent className="bg-zinc-950 border border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-drill-dialog">
          {!drillData ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="text-orange-500 animate-spin" size={32} />
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-1">
                  Drill-down · {drillData.user.role.replace("_", " ")}
                </div>
                <DialogTitle className="font-outfit text-3xl tracking-tighter">
                  {drillData.user.name || drillData.user.email}
                </DialogTitle>
                <p className="text-zinc-400 text-sm mt-1">
                  {drillData.user.email}
                  {drillData.user.company && ` · ${drillData.user.company}`}
                  {drillData.user.phone && ` · ${drillData.user.phone}`}
                </p>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="border border-white/10 rounded-md p-3 bg-zinc-900/30">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Simulări</div>
                  <div className="font-outfit text-2xl font-black text-orange-500 mt-1">
                    {drillData.stats.simulations_total}
                  </div>
                </div>
                <div className="border border-white/10 rounded-md p-3 bg-zinc-900/30">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recomandări</div>
                  <div className="font-outfit text-2xl font-black text-white mt-1">
                    {drillData.stats.recommendations_total}
                  </div>
                </div>
                <div className="border border-white/10 rounded-md p-3 bg-zinc-900/30">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Logins</div>
                  <div className="font-outfit text-2xl font-black text-white mt-1">
                    {drillData.user.login_count || 0}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="font-outfit font-bold text-white mb-3 text-sm uppercase tracking-[0.2em]">Stiluri preferate</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(drillData.stats.style_breakdown).map(([k, v]) => (
                    <span key={k} className="px-3 py-1 border border-orange-500/30 bg-orange-500/5 rounded-full text-xs text-orange-400">
                      {k.replace("_", " ")} · {v}
                    </span>
                  ))}
                  {Object.keys(drillData.stats.style_breakdown).length === 0 && (
                    <span className="text-zinc-500 text-sm">Nicio simulare încă.</span>
                  )}
                </div>
              </div>

              {drillData.stats.categories_popularity.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-outfit font-bold text-white mb-3 text-sm uppercase tracking-[0.2em]">
                    Categorii FDP alese
                  </h4>
                  <div className="space-y-1.5">
                    {drillData.stats.categories_popularity.slice(0, 8).map((c) => {
                      const m = drillData.stats.categories_popularity[0]?.count || 1;
                      return <Bar2 key={c.slug} label={c.name} count={c.count} max={m} />;
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <h4 className="font-outfit font-bold text-white mb-3 text-sm uppercase tracking-[0.2em]">
                  Simulări recente ({drillData.simulations.length})
                </h4>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {drillData.simulations.slice(0, 30).map((s) => (
                    <Link
                      key={s.id}
                      to={`/simulation/${s.id}`}
                      onClick={() => {
                        setDrillUserId(null);
                        setDrillData(null);
                      }}
                      className="flex items-center justify-between text-xs px-3 py-2 border border-white/5 rounded-md hover:border-orange-500/30 hover:bg-zinc-900/40"
                    >
                      <span className="text-zinc-300">
                        {s.sim_type} · {s.style.replace("_", " ")}
                        {s.client_name ? ` · ${s.client_name}` : ""}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={
                          s.status === "completed"
                            ? "text-green-400"
                            : s.status === "failed"
                            ? "text-red-400"
                            : "text-orange-400"
                        }>
                          {s.status}
                        </span>
                        <span className="text-zinc-500">{formatDate(s.created_at)}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDeleteSim}
        onOpenChange={(v) => !deletingSim && !v && setConfirmDeleteSim(null)}
      >
        <AlertDialogContent className="bg-zinc-950 border-white/10" data-testid="admin-delete-sim-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Șterge simularea?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Această acțiune este ireversibilă. Imaginea sursă și toate
              variațiile generate vor fi șterse din baza de date și storage.
              {confirmDeleteSim && (
                <span className="block mt-2 text-zinc-500 text-xs">
                  {confirmDeleteSim.user_email} · {confirmDeleteSim.sim_type} ·{" "}
                  {confirmDeleteSim.style?.replace("_", " ")}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deletingSim}
              className="bg-transparent border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white"
              data-testid="admin-delete-sim-cancel"
            >
              Anulează
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeleteSim}
              disabled={deletingSim}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="admin-delete-sim-confirm"
            >
              {deletingSim ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              Șterge definitiv
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkCleanupOpen} onOpenChange={(v) => !bulkLoading && setBulkCleanupOpen(v)}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 size={18} className="text-red-400" /> Curățenie simulări
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-amber-200 text-xs leading-relaxed">
              <strong>Atenție.</strong> Această acțiune șterge definitiv simulările +
              fișierele lor din storage. Simulările cu imagini „pinned" sau „featured"
              pe homepage / în galeria publică sunt automat protejate și NU vor fi
              șterse. Această protecție nu poate fi dezactivată din acest panou.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1 block">
                  Doar simulări mai vechi de (zile)
                </Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0 = toate"
                  value={bulkFilter.older_than_days}
                  onChange={(e) => setBulkFilter((s) => ({ ...s, older_than_days: e.target.value }))}
                  className="bg-zinc-900 border-white/10 text-white"
                  data-testid="bulk-cleanup-older-than-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1 block">
                  Doar pentru user (email)
                </Label>
                <Input
                  type="email"
                  placeholder="opțional"
                  value={bulkFilter.user_email}
                  onChange={(e) => setBulkFilter((s) => ({ ...s, user_email: e.target.value }))}
                  className="bg-zinc-900 border-white/10 text-white"
                  data-testid="bulk-cleanup-user-email-input"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => runBulkPreview(bulkFilter)}
                disabled={bulkLoading}
                className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                data-testid="bulk-cleanup-refresh-preview-btn"
              >
                {bulkLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Recalculează preview
              </Button>
            </div>

            {bulkPreview && (
              <div className="rounded border border-white/10 bg-zinc-900/50 p-4 space-y-2" data-testid="bulk-cleanup-preview">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Vor fi șterse
                    </div>
                    <div className="text-3xl font-bold text-red-400 mt-1" data-testid="bulk-cleanup-to-delete-count">
                      {bulkPreview.to_delete_count}
                    </div>
                    <div className="text-xs text-zinc-500">simulări</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Protejate (featured/pinned)
                    </div>
                    <div className="text-3xl font-bold text-emerald-400 mt-1" data-testid="bulk-cleanup-protected-count">
                      {bulkPreview.protected_featured_count}
                    </div>
                    <div className="text-xs text-zinc-500">simulări păstrate</div>
                  </div>
                </div>

                {bulkPreview.sample && bulkPreview.sample.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                      Primele {bulkPreview.sample.length} simulări (sample)
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1 text-xs">
                      {bulkPreview.sample.map((s) => (
                        <div key={s.id} className="flex justify-between text-zinc-400 border-b border-white/5 pb-1">
                          <span className="truncate flex-1">
                            {s.user_email} · {s.sim_type} · {s.style?.replace("_", " ")}
                          </span>
                          <span className="text-zinc-600 ml-2">
                            {s.image_count}img · {s.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setBulkCleanupOpen(false)}
                disabled={bulkLoading}
                className="text-zinc-400 hover:text-white"
                data-testid="bulk-cleanup-cancel-btn"
              >
                Anulează
              </Button>
              <Button
                onClick={doBulkCleanup}
                disabled={bulkLoading || !bulkPreview || bulkPreview.to_delete_count === 0}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="bulk-cleanup-confirm-btn"
              >
                {bulkLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
                Șterge {bulkPreview?.to_delete_count || 0} simulări
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- QuoteRequestsTab -------------------------------------------------------
function QuoteRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let m = true;
    api
      .get("/admin/quote-requests")
      .then((r) => m && setItems(r.data))
      .catch((e) =>
        toast.error(
          e.response?.data?.detail || "Eroare la încărcarea solicitărilor"
        )
      )
      .finally(() => m && setLoading(false));
    return () => {
      m = false;
    };
    // `api` and `toast` are module-level imports (stable). Setters are stable
    // by React contract. Empty-deps array is intentional: fetch once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center text-zinc-400 text-sm">
        <Loader2 size={14} className="mr-2 animate-spin" /> Se încarcă...
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="text-center text-zinc-500 py-12 border border-white/5 rounded-lg bg-zinc-900/20">
        <div className="text-sm">Nicio solicitare de ofertă încă.</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-zinc-500" data-testid="quote-requests-count">
        {items.length} solicitări · trimise către sales@fabricadeprofile.ro
        (momentan în mod MOCK — log în consolă)
      </div>
      <div className="border border-white/10 rounded-lg bg-zinc-900/20 divide-y divide-white/5">
        {items.map((q) => {
          const open = expanded === q.id;
          return (
            <div key={q.id} data-testid={`quote-request-${q.id}`}>
              <button
                type="button"
                onClick={() => setExpanded(open ? null : q.id)}
                className="w-full text-left px-4 py-3 hover:bg-white/5 transition"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium truncate">
                      {q.user_data?.name || q.user_email}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">
                      {q.user_email}
                      {q.user_data?.phone && ` · ${q.user_data.phone}`}
                      {q.user_data?.company && ` · ${q.user_data.company}`}
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500">
                    {formatDate(q.created_at)}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                    {q.simulations_snapshot?.length || 0} sims
                  </div>
                </div>
              </button>
              {open && (
                <div className="px-4 pb-4 bg-black/30 space-y-3 text-sm">
                  {q.message && (
                    <div className="border-l-2 border-orange-500/40 pl-3 py-1">
                      <div className="text-[10px] uppercase tracking-wider text-orange-400 mb-1">
                        Mesaj
                      </div>
                      <div className="text-zinc-300 whitespace-pre-wrap">
                        {q.message}
                      </div>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    {q.user_data?.vat_id && (
                      <div>
                        <span className="text-zinc-500">CUI:</span>{" "}
                        <span className="text-zinc-300">{q.user_data.vat_id}</span>
                      </div>
                    )}
                    {q.user_data?.person_type && (
                      <div>
                        <span className="text-zinc-500">Tip:</span>{" "}
                        <span className="text-zinc-300">{q.user_data.person_type}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-zinc-500">Rol:</span>{" "}
                      <span className="text-zinc-300">{q.user_data?.role}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Trimis la:</span>{" "}
                      <span className="text-zinc-300">{q.sent_to}</span>
                    </div>
                  </div>
                  {q.simulations_snapshot?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                        Simulări atașate
                      </div>
                      <ul className="space-y-1">
                        {q.simulations_snapshot.map((s) => (
                          <li
                            key={s.id}
                            className="flex items-center gap-2 text-xs text-zinc-300"
                          >
                            <span className="text-orange-400 font-mono">
                              {s.sim_type}
                            </span>
                            <span className="text-zinc-500">·</span>
                            <span>{s.style?.replace("_", " ")}</span>
                            <span className="text-zinc-500">·</span>
                            <span className="text-zinc-500">{s.render_mode}</span>
                            <span className="text-zinc-500">·</span>
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-orange-400 hover:text-orange-300 ml-auto"
                            >
                              vezi →
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {q.products_used?.length > 0 && (
                    <div
                      className="mt-2 pt-2 border-t border-white/5"
                      data-testid={`quote-products-used-${q.id}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                        Produse FDP folosite — click deschide categoria pe shop
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {q.products_used.map((p) => (
                          <a
                            key={p.code}
                            href={p.url || "#"}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center px-2 py-1 rounded bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/20 hover:text-orange-200 text-xs font-mono uppercase transition"
                            title={`Deschide categoria ${p.category_slug || ""} pe fabricadeprofile.ro — caută ${p.code}`}
                          >
                            cp {p.code}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- ErpIntegrationTab ------------------------------------------------------
function ErpIntegrationTab() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/erp/status"),
        api.get(`/admin/erp/log?limit=100${onlyFailed ? "&only_failed=true" : ""}`),
      ]);
      setStatus(s.data);
      setLog(l.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyFailed]);

  const retry = async (id) => {
    setRetrying(id);
    try {
      const r = await api.post(`/admin/erp/retry/${id}`);
      toast.success(
        r.data.ok
          ? "Sync retrimis cu succes"
          : `Retry eșuat: ${r.data.error || "necunoscut"}`
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry eșuat");
    } finally {
      setRetrying(null);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center text-zinc-400 text-sm">
        <Loader2 size={14} className="mr-2 animate-spin" /> Se încarcă...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status banner */}
      {status && (
        <div
          className={`rounded border p-4 ${
            status.configured
              ? "border-emerald-500/30 bg-emerald-500/5"
              : "border-amber-500/30 bg-amber-500/5"
          }`}
          data-testid="erp-status-banner"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">
              Status integrare ERP — ultimele 24h
            </div>
            <div className="text-[10px] font-mono text-zinc-500 truncate max-w-md">
              {status.base_url}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Configurat
              </div>
              <div className={`text-2xl font-bold mt-1 ${status.configured ? "text-emerald-400" : "text-amber-400"}`}>
                {status.configured ? "DA" : "NU"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Total
              </div>
              <div className="text-2xl font-bold text-zinc-200 mt-1">
                {status.last_24h.total}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Reușite
              </div>
              <div className="text-2xl font-bold text-emerald-400 mt-1">
                {status.last_24h.ok}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Eșuate
              </div>
              <div className="text-2xl font-bold text-red-400 mt-1">
                {status.last_24h.failed}
              </div>
            </div>
          </div>
          {status.last_24h.skipped_no_consent > 0 && (
            <div className="text-[10px] text-zinc-500 mt-2">
              {status.last_24h.skipped_no_consent} ignorate (fără consimțământ GDPR)
            </div>
          )}
        </div>
      )}

      {/* Filter + refresh */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-400">
          <input
            type="checkbox"
            checked={onlyFailed}
            onChange={(e) => setOnlyFailed(e.target.checked)}
            className="accent-orange-500"
            data-testid="erp-only-failed"
          />
          Doar eșuate
        </label>
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={loading}
          className="text-zinc-400 hover:text-white"
          data-testid="erp-refresh"
        >
          {loading ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
          Reîmprospătează
        </Button>
        <span className="text-[10px] text-zinc-500 ml-auto">
          {log.length} entries
        </span>
      </div>

      {/* Log list */}
      {log.length === 0 ? (
        <div className="text-center text-zinc-500 py-8 border border-white/5 rounded bg-zinc-900/20 text-sm">
          {onlyFailed ? "Niciun sync eșuat" : "Niciun sync încă"}
        </div>
      ) : (
        <div className="border border-white/10 rounded bg-zinc-900/20 divide-y divide-white/5 text-sm">
          {log.map((entry) => (
            <div
              key={entry.id}
              className="px-4 py-3"
              data-testid={`erp-log-${entry.id}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono ${
                      entry.skipped
                        ? "bg-zinc-700/50 text-zinc-400"
                        : entry.ok
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-red-500/20 text-red-300"
                    }`}
                  >
                    {entry.skipped ? "SKIP" : entry.ok ? "OK" : "ERR"} {entry.status_code || ""}
                  </span>
                  <span className="text-xs font-mono text-orange-400">{entry.path}</span>
                  <span className="text-[11px] text-zinc-500">
                    {entry.payload?.external_id || entry.payload?.email}
                  </span>
                  {entry.payload?.event_name && (
                    <span className="text-[11px] text-zinc-400 italic">
                      {entry.payload.event_name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-600 font-mono">
                    {entry.started_at?.slice(11, 19)}
                  </span>
                  {!entry.ok && !entry.skipped && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => retry(entry.id)}
                      disabled={retrying === entry.id}
                      className="text-orange-400 hover:text-orange-300 text-xs h-6 px-2"
                      data-testid={`erp-retry-${entry.id}`}
                    >
                      {retrying === entry.id ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        "Retry"
                      )}
                    </Button>
                  )}
                </div>
              </div>
              {!entry.ok && !entry.skipped && entry.error && (
                <div className="mt-2 text-[11px] text-red-300/70 font-mono break-all">
                  {entry.error.length > 200
                    ? entry.error.slice(0, 200) + "…"
                    : entry.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
